///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../abstract-command';
import { ActivityDTO } from '../../../../services/project-socket/activity-dto.model';
import { GtthPreloaderModalEvent } from '../../../../shared/preloader-modal/preloader-modal-event';
import { GtthModifyUnitInfoCommand } from './node/modify-unit-info-command';
import { GtthModifyLinkInfoCommand } from './link/modify-link-info-command';
import { GtthLinkFeatureDtoModel } from '../../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { GtthNodeFeatureDtoModel } from '../../../../services/project-socket/dto/gtth-node-feature-dto.model';

export class GtthDisableMultipleEntitiesCommand extends GtthAbstractCommand {
    private linkIds: number[];
    private terminalIds: number[];

    private terminalUnitsToDisable: GtthNodeFeatureDtoModel[];
    private linksToDisable: GtthLinkFeatureDtoModel[];

    getCommandName(): string {
        return 'GtthDisableMultipleEntitiesCommand';
    }

    createFromNodeId(terminalId: number) {
        this.terminalIds = [terminalId];
        return this;
    }

    createFromLinkId(linkId: number) {
        this.linkIds = [linkId];
        return this;
    }

    createFromList(terminalIds: number[], linkIds: number[]) {
        this.terminalIds = terminalIds;
        this.linkIds = linkIds;
        return this;
    }

    doCommand(): void {
        this.historyService.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.show('Loading')
        });

        const logicalTopology = this.historyService.logicalTopology;

        if (!this.linkIds || !this.terminalIds) {
            this.linkIds = this.linkIds || [];
            this.terminalIds = this.terminalIds || [];

            for (const link of this.historyService.logicalTopology.getSelectedLinks()) {
                this.linkIds.push(link.getId());
            }
            for (const station of this.historyService.logicalTopology.getSelectedStations()) {
                this.terminalIds.push(station.getId());
            }
        }

        this.linksToDisable = [];
        this.terminalUnitsToDisable = [];

        if (this.terminalIds === undefined) {
            this.terminalIds = [];
        }

        this.terminalIds.forEach((entityId: number) => {
            const terminalStation = logicalTopology.getStation(entityId);

            if (!terminalStation.hasFreezeLinks()) {
                if (terminalStation.isEnabled()) {
                    const terminalGeoJson = terminalStation.toGeoJson();
                    terminalGeoJson.setIsEnabled(false);
                    this.terminalUnitsToDisable.push(terminalGeoJson);
                }

                logicalTopology.getAllStationLinks(entityId).forEach((link) => {
                    if (!link.getLinkIsDisabled()) {
                        const linkExist = this.linksToDisable.find((innerLink) => {
                            return innerLink.getId() === link.getId();
                        });
                        if (!linkExist) {
                            const linkGeoJson = link.toGeoJson();
                            linkGeoJson.setIsDisabled(true);
                            this.linksToDisable.push(linkGeoJson);
                        }
                    }
                });
            }
        });

        if (this.linkIds === undefined) {
            this.linkIds = [];
        }

        this.linkIds.forEach((entityId: number) => {
            const linkToDisable = logicalTopology.getLink(entityId);

            if (!linkToDisable.isUserForced() && !linkToDisable.getLinkIsDisabled()) {
                const linkExist = this.linksToDisable.find((link) => {
                    return link.getId() === linkToDisable.getId();
                });
                if (!linkExist) {
                    const linkGeoJson = linkToDisable.toGeoJson();
                    linkGeoJson.setIsDisabled(true);
                    this.linksToDisable.push(linkGeoJson);
                }
            }
        });

        const activity = this.createActivityDTO(false);
        activity.links = [...this.linksToDisable];
        activity.nodes = [...this.terminalUnitsToDisable];
        this.sendActivity(activity);
    }

    undoCommand(): void {
        this.historyService.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.show('Loading')
        });

        this.terminalUnitsToDisable.forEach((terminalGeoJson: GtthNodeFeatureDtoModel) => terminalGeoJson.setIsEnabled(true));

        this.linksToDisable.forEach((linkGeoJson: GtthLinkFeatureDtoModel) => linkGeoJson.setIsDisabled(false));

        const activity = this.createActivityDTO(false);
        activity.links = [...this.linksToDisable];
        activity.nodes = [...this.terminalUnitsToDisable];
        this.sendActivity(activity);
    }

    doCommandActivity(activityDto: ActivityDTO): void {
        this.handleCommandActivity(activityDto);
    }

    undoCommandActivity(activityDto: ActivityDTO): void {
        this.handleCommandActivity(activityDto);
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    isModifyTopology(): boolean {
        return true;
    }

    isClearDesign(): boolean {
        return true;
    }

    private handleCommandActivity(activityDto: ActivityDTO): void {
        if (!activityDto.successful) {
            this.historyService.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.hide()
            });

            this.historyService.notificationService.showError(activityDto?.error?.message);

            if (this.terminalUnitsToDisable && this.linksToDisable) {
                console.log('Command failed:', activityDto.error);
            }

            if (this.commandResolve) {
                this.commandResolve(activityDto.successful);
            }
        } else {
            this.changeEntities(activityDto.nodes, activityDto.links).then((result: boolean) => {
                if (this.commandResolve) {
                    this.commandResolve(result);
                }

                this.historyService.eventManager.broadcast({
                    name: 'togglePreloaderModal',
                    eventObject: GtthPreloaderModalEvent.hide()
                });
            });
        }
    }

    private changeEntities(nodes: GtthNodeFeatureDtoModel[], links: GtthLinkFeatureDtoModel[]): Promise<boolean> {
        const nodesDisableCommands = [];
        const linksDisableCommands = [];

        if (nodes === undefined) {
            nodes = [];
        }
        nodes.forEach((terminalGeoJson: GtthNodeFeatureDtoModel) => {
            const unitModifyCommand = this.historyService.command(GtthModifyUnitInfoCommand).create(terminalGeoJson.getId());
            unitModifyCommand.setNewIsEnableValue(terminalGeoJson.getIsEnabled());
            nodesDisableCommands.push(this.historyService.doCommandBypassHistory(unitModifyCommand, true));
        });

        return Promise.all(nodesDisableCommands).then((_nodesDisableResult) => {
            if (links === undefined) {
                links = [];
            }
            links.forEach((linkGeoJson: GtthLinkFeatureDtoModel) => {
                const linkId = linkGeoJson.getId();
                const linkModifyCommand = this.historyService.command(GtthModifyLinkInfoCommand).create(linkId);
                linkModifyCommand.setOldLinkIsDisable(this.historyService.logicalTopology.getLink(linkId).getLinkIsDisabled());
                linkModifyCommand.setNewLinkIsDisable(linkGeoJson.getIsDisabled());
                linksDisableCommands.push(this.historyService.doCommandBypassHistory(linkModifyCommand, true));
            });

            return Promise.all(linksDisableCommands).then((_linksDisableResult) => {
                return true;
            });
        });
    }
}
